import { extractBool } from './misc';

type QueryStringValue = string | null | undefined;

const decodeQueryParam = (val: string): string => decodeURIComponent(val.replace(/\+/g, ' '));

/**
 * Converts URL query string to object with values
 * @param {string} query
 * @returns {object}
 */
export const parseUrlQueryString = (query: string): Record<string, string> =>
    Object.fromEntries(
        (query[0] === '?' ? query.substring(1) : query).split('&').map(item => {
            const [key, val = ''] = item.split('=');
            return [decodeURIComponent(key), decodeQueryParam(val)];
        })
    );

export const extractFromQueryString = (
    key: string,
    defaultValue?: QueryStringValue
): QueryStringValue => parseUrlQueryString(window.location.search)[key] ?? defaultValue;

export const extractBooleanFromQuery = (key: string): boolean | undefined =>
    extractBool(extractFromQueryString(key));

/**
 * Removes query parameter(s) from the given url
 */
export const removeQueryParamFromURL = (key: string | string[], url: string): string => {
    const urlObject = new URL(url);
    const keys = Array.isArray(key) ? key : [key];

    keys.forEach(key => urlObject.searchParams.delete(key));

    return urlObject.toString();
};

/**
 * Checks a special meta tag and returns its baseroot value
 * It is required for serving app from some custom subroot
 * at the moment used on k8s cluster, where this parameter is replaced by nginx
 */
const getBaseRoot = () => {
    const root =
        (typeof self == 'object' && self.self === self && self) ||
        (typeof global == 'object' && global.global === global && global);

    if (!root || !root.document) {
        return '/';
    }

    const $baseRoot = root.document.querySelector('meta[baseroot]');
    return ($baseRoot && $baseRoot.getAttribute('baseroot')) ?? '/';
};

export const BASE_ROOT = getBaseRoot();

export const encodeQueryString = params =>
    Object.entries(params)
        .map(param =>
            param
                .filter(v => v !== undefined)
                .map(v => encodeURIComponent(String(v)))
                .join('=')
        )
        .join('&');

/**
 * Casts query params to string and joins it to a given URL
 * @param {string} baseURL
 * @param {Object.<string, string|number>} params - query params as object
 * @returns {string} wrapped URL
 */
export const wrapWithQueryParams = (baseURL: string, params: Record<string, string> = {}) => {
    if (!Object.values(params).length) {
        return baseURL;
    }

    const url = new URL(baseURL);

    Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));

    return url.toString();
};

/**
 * Splits current path with / and returns the last part
 * @returns {string} pathSuffix
 */
export const getCurrentPathSuffix = (): string => {
    const splitPath = window.location.href.split('/');
    return splitPath[splitPath.length - 1];
};

/**
 * Checks that the second level domain is the same for two urls
 * @param url1 -  url to compare, ex. https://v.ringcentral.com
 * @param url2 - url to compare, ex. https://app.ringcentral.com
 */
export function checkIsSameSite(url1, url2) {
    const { protocol: protocol1, origin: origin1 } = new URL(url1);
    const { protocol: protocol2, origin: origin2 } = new URL(url2);

    if (protocol1 !== protocol2) return false;

    const getTLDPlusOne = url => url.split('.').slice(-2).join('.');
    const topLevelDomainPlusOne1 = getTLDPlusOne(origin1);
    const topLevelDomainPlusOne2 = getTLDPlusOne(origin2);

    return topLevelDomainPlusOne1 === topLevelDomainPlusOne2;
}
